import type { FocusEvent } from 'react';
import React, { useMemo, useEffect, useState } from 'react';
import get from 'lodash/get';
import debounce from 'lodash/debounce';
import { Dropdown as SDropdown } from 'semantic-ui-react';
import uniq from 'lodash/uniq';
import type { DropdownItemProps } from 'semantic-ui-react/dist/commonjs/modules/Dropdown/DropdownItem';

import { AsyncConnector } from '../../../classes/async-connector';
import { Button } from '../../element/Button/Button';
import { IconButton } from '../../element/Button/IconButton';
import { Icon } from '../../element/Icon/Icon';
import { DesignSysEnvUtil } from '../../../utils/design-sys-env-util';
import { AttrsHelper } from '../../../sb-helpers/attrs-helper';

type ExistingRawDropdownValueType = string | number | boolean;
export type RawDropdownValueType =
  | ExistingRawDropdownValueType
  | null
  | undefined
  | Array<ExistingRawDropdownValueType | undefined | null>;

type OptionStructure = {
  key: ExistingRawDropdownValueType | null | undefined;
  text: string;
  value: ExistingRawDropdownValueType | null | undefined;
};

type OptionsStructure = {
  options: OptionStructure[];
  totalPages: number;
  currentPage: number;
  searchQuery: string;
};

export type RawDropdownDirectionType = 'up' | 'down' | 'default';

export const DirectionMap = {
  up: true,
  down: false,
};

export type RawDropdownProps<AsyncConnectorResult> = {
  allowAdditions?: boolean;
  disableArrowKeyChange?: boolean;
  direction?: RawDropdownDirectionType;
  displayKey: string;
  disabled?: boolean;
  hideIcon?: boolean;
  id?: string;
  invalid?: boolean;
  multiple?: boolean;
  name: string;
  onAddItem?: (value: RawDropdownValueType) => void;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  onChange: (
    value: RawDropdownValueType,
    e?: React.SyntheticEvent<HTMLElement>,
    valueOption?: Record<string, unknown> | Array<Record<string, unknown>>
  ) => Promise<unknown> | void;
  onFocus: (e: React.FocusEvent<HTMLInputElement>) => void;
  onType?: (value: RawDropdownValueType) => Promise<void> | void;
  options:
    | Array<Record<string, unknown>>
    | AsyncConnector<AsyncConnectorResult>
    | ReadonlyArray<Record<string, unknown>>;
  placeholder?: string | null;
  value?: RawDropdownValueType;
  valueKey: string;
};

const divider = ':||:';

const convertValue = (value: RawDropdownValueType) => {
  return Array.isArray(value) ? value.join(divider) : value;
};
export const RawDropdown = <AsyncConnectorResult,>({
  allowAdditions,
  disableArrowKeyChange,
  displayKey,
  direction = 'default',
  disabled = false,
  hideIcon,
  id,
  invalid = false,
  multiple = false,
  name,
  onAddItem,
  onBlur,
  onChange,
  onFocus,
  onType,
  options,
  placeholder,
  value,
  valueKey,
}: RawDropdownProps<AsyncConnectorResult>) => {
  const isAsyncOptions = options instanceof AsyncConnector;
  const dropdownIconName = 'default-dropdown-arrow';
  const getDefaultOptionsState = () => {
    return {
      options: isAsyncOptions ? ([] as OptionStructure[]) : options,
      totalPages: 1,
      currentPage: 1,
      searchQuery:
        typeof value === 'string' ? value : JSON.stringify(value, null, 2),
    } as OptionsStructure;
  };

  const [optionsState, setOptionsState] = useState<OptionsStructure>(
    getDefaultOptionsState()
  );
  const [loading, setLoading] = useState({
    isLoading: false,
    hasLoaded: false,
  });

  if (multiple && isAsyncOptions) {
    // BE endpoints that want typeahead for a multi select needs the search to be inclusive and searchable by IDs
    throw new Error(
      `Field '${name}' expects an array of values, however the dropdown currently does not support async search for multi select`
    );
  }

  const dropdownOptions = useMemo(() => {
    return optionsState.options.reduce<
      Array<{
        key: ExistingRawDropdownValueType | null | undefined;
        text: string;
        value: ExistingRawDropdownValueType | null | undefined;
      }>
    >((acc, o) => {
      const display = get(o, displayKey) as string;
      const v = get(o, valueKey) as ExistingRawDropdownValueType;
      if (display) {
        if (Array.isArray(v) && !multiple) {
          throw new Error(
            `Option '${display}' has an array of values but the schema prop for '${name}' does not accept an array of values`
          );
        }
        acc.push({
          key: convertValue(v),
          text: display,
          value: convertValue(v),
        });
      }

      return acc;
    }, []);
  }, [optionsState.options]);

  // loads async data into dropdown options
  const loadDataSource = (searchQuery: string, page = 1) => {
    if (isAsyncOptions) {
      setLoading({ isLoading: true, hasLoaded: false });

      void options
        .getData({ search: searchQuery, page, sort: {} })
        .then(response => {
          const {
            data,
            totalPages: tPages,
            currentPage: cPage,
          } = options.parseData(response);

          setOptionsState(prevState => {
            const { options: opts } = prevState;
            return {
              options: page > 1 ? [...opts, ...(data || [])] : data,
              totalPages: tPages,
              currentPage: cPage,
              searchQuery,
            } as OptionsStructure;
          });
          setLoading({ isLoading: false, hasLoaded: true });
        });
    }
  };

  const debounceSearch = useMemo(
    () =>
      debounce(s => {
        loadDataSource(s);
      }, DesignSysEnvUtil.typingDebounce),
    []
  );

  // Sets the dropdown options as expected if not async
  useEffect(() => {
    if (!isAsyncOptions) {
      setOptionsState(
        state =>
          ({
            ...state,
            options,
          }) as OptionsStructure
      );
      setLoading(state => ({
        ...state,
        hasLoaded: true,
      }));
    }
  }, [options]);

  // ensure dropdown options contain the value upon initialization
  // so the value displays as selected properly
  useEffect(() => {
    if (value) {
      loadDataSource(
        typeof value === 'string' ? value : JSON.stringify(value, null, 2)
      );
    } else {
      setLoading(state => ({
        ...state,
        hasLoaded: true,
      }));
    }
  }, []);

  if (multiple && (value === undefined || value === null)) {
    value = [];
  }

  // Pass value as null instead of undefined because semantic
  // will retain previous value, if updated with undefined.
  if (!multiple && value === undefined) {
    value = null;
  }

  const hasValue = multiple
    ? Array.isArray(value) && value.length > 0
    : !!value || value === 0;

  const getDropdownOptions = () => {
    const loadMoreOption = {
      key: 'LOAD_MORE',
      content: (
        <Button
          name="LOAD_MORE_OPTIONS"
          onClick={e => {
            e.stopPropagation();
            loadDataSource(
              optionsState.searchQuery,
              optionsState.currentPage + 1
            );
          }}
        >
          Load More
        </Button>
      ),
    };

    return optionsState.currentPage < optionsState.totalPages
      ? [...dropdownOptions, loadMoreOption]
      : dropdownOptions;
  };

  return (
    <div data-test={name}>
      <SDropdown
        className={AttrsHelper.formatClassname(
          loading.hasLoaded && 'has-loaded'
        )}
        allowAdditions={allowAdditions}
        clearable
        upward={DirectionMap[direction as keyof typeof DirectionMap]}
        disabled={disabled}
        error={invalid}
        fluid
        icon={
          loading.isLoading
            ? undefined
            : !hasValue &&
              !hideIcon && (
                <IconButton
                  pattern="secondary"
                  variation="minimal"
                  icon="arrow-down"
                  name={dropdownIconName}
                  className="control-icon fixed-dimension-inline"
                />
              )
        }
        id={id}
        multiple={multiple}
        onAddItem={(_e, data) => {
          onAddItem?.(data.value);
        }}
        onBlur={(event, data) => {
          if (isAsyncOptions && !data.value) {
            setOptionsState(getDefaultOptionsState());
          }
          if (event.target.dataset.test !== dropdownIconName) {
            onBlur(event as FocusEvent<HTMLInputElement>);
          }
        }}
        onChange={(e, data) => {
          const formattedValue = Array.isArray(data.value)
            ? uniq(
                data.value
                  .reduce<RawDropdownValueType[]>((acc, v) => {
                    if (typeof v === 'string') {
                      acc.push(v.includes(divider) ? v.split(divider) : v);
                    } else {
                      acc.push(v);
                    }

                    return acc;
                  }, [])
                  .flat()
              )
            : data.value;

          const valueOption = Array.isArray(formattedValue)
            ? optionsState.options.filter(opt =>
                formattedValue.includes(get(opt, valueKey))
              )
            : optionsState.options.find(
                opt => get(opt, valueKey) === formattedValue
              );

          void onChange(formattedValue, e, valueOption);
        }}
        onFocus={event => {
          onFocus(event as FocusEvent<HTMLInputElement>);
        }}
        onSearchChange={(_e, data) => {
          if (onType) {
            void onType(data.searchQuery);
          }
          if (isAsyncOptions) {
            debounceSearch(data.searchQuery);
          }
        }}
        options={getDropdownOptions() as DropdownItemProps[]}
        // @ts-expect-error Type is wrong with Semantic. It does take null just fine
        placeholder={placeholder}
        renderLabel={({ text }) => {
          // TODO: remove this in Semantic v3
          // This maintains compatibility with Shorthand API in v1 as this might be called in "Label.create()"
          if (typeof text === 'function') {
            return text;
          }

          return {
            content: (
              <>
                {text}
                <span
                  className="pill-control-icon"
                  onClick={e => {
                    e.stopPropagation();
                    const foundPillItem = dropdownOptions.find(
                      op => op.text === text
                    );
                    const updatedValue = (value as string[]).filter(
                      v => v !== foundPillItem?.value
                    );

                    void onChange(
                      updatedValue,
                      e,
                      optionsState.options.filter(opt =>
                        updatedValue.includes(get(opt, valueKey))
                      )
                    );
                  }}
                >
                  <Icon name="x-mark" />
                </span>
              </>
            ),
          };
        }}
        // for async, options are determined by the response, otherwise use default filtering of the dropdown
        search={!isAsyncOptions || (opt => opt)}
        selection
        selectOnBlur={false}
        selectOnNavigation={!disableArrowKeyChange}
        scrolling
        value={
          value as boolean | number | string | Array<boolean | number | string>
        }
        loading={loading.isLoading}
      />
    </div>
  );
};
